import Image from 'next/image';
import Link from 'next/link';

import { ISanitizedCompany } from 'types/company';

import { useEmailSignUp } from 'hooks/auth/sign';

import { AuthForm } from './AuthForm';
import { GoogleSignInButton } from './GoogleSignInButton';
import { useAuthGateStore } from 'stores/authGateStore';

interface IProps {
  switchSection(): void;
  company?: ISanitizedCompany;
}

export const SignUp = ({ switchSection, company }: IProps) => {
  const submitLabel = 'Register with email';
  const authStatus = useAuthGateStore(state => state.status);
  const { emailSignUpHandler, pending } = useEmailSignUp();

  const shouldPreventInteraction =
    authStatus === 'checking' || authStatus === 'redirecting';

  return (
    <article>
      <header className="flex flex-col items-center">
        <p className="text-sm font-[500]">
          <span className="text-black opacity-50 dark:text-white">
            Existing User? &nbsp;
          </span>

          <button
            type="button"
            className="cursor-pointer bg-transparent p-0 text-carnationRed hover:underline"
            onClick={switchSection}
            disabled={shouldPreventInteraction}
          >
            Log-in
          </button>
        </p>

        {!!company?.logoUrl && !!company?.logoDarkUrl && (
          <>
            <div className="relative mt-6 block h-[45px] w-full dark:hidden">
              <Image
                src={company.logoUrl}
                alt={`${company.name} logo`}
                fill
                sizes="(max-width: 640px) 100vw, 400px"
                preload
                style={{ objectFit: 'contain', objectPosition: 'center' }}
              />
            </div>

            <div className="relative mt-6 hidden h-[45px] w-full dark:block">
              <Image
                src={company.logoDarkUrl}
                alt={`${company.name} logo`}
                fill
                sizes="(max-width: 640px) 100vw, 400px"
                preload
                style={{ objectFit: 'contain', objectPosition: 'center' }}
              />
            </div>
          </>
        )}

        <h2 className="mb-2 mt-10 text-center text-[46px] font-bold leading-tight text-carnationRed">
          Registration
        </h2>

        <p className="mb-9 text-center text-lg font-thin">
          Welcome to acehub. Create a free account and start enjoying your
          company stories.
        </p>
      </header>

      <div>
        <GoogleSignInButton />
      </div>

      <div className="my-8 flex flex-row items-center justify-between">
        <div className="flex-grow border-b border-b-[#C4C4C4]" />

        <p className="mx-2 text-[10px] font-semibold uppercase text-[#C4C4C4]">
          or
        </p>

        <div className="flex-grow border-b border-b-[#C4C4C4]" />
      </div>

      <AuthForm
        onSubmit={emailSignUpHandler}
        submitLabel={submitLabel}
        loading={pending}
        shouldPreventInteraction={shouldPreventInteraction}
      />

      <p className="mt-3 text-center text-xs text-black opacity-50 dark:text-white">
        By continuing, you agree to acehub’s &nbsp;
        <Link
          href="https://acehub.io/terms-conditions"
          target="_blank"
          rel="noreferrer noopener"
        >
          <span className="font-bold hover:underline">
            Terms &amp; Conditions
          </span>
        </Link>
      </p>
    </article>
  );
};
